import React, { useState, useEffect } from 'react'
import Footer from '../components/footer'
import Navbar from '../components/navbar'
import Button from '../components/input-components/button'
import TextField from '../components/input-components/textfield'
import {
  DonationType,
  PAYMENT_PLATFORMS,
} from '../types/donation'
import { campaignsApi, Campaign, CampaignAnalytics, CampaignStatus } from '../services/campaigns-api'
import { donationsApi, DonationType as ApiDonationType } from '../services/donations-api'

// Helper to format currency (amount is in pence)
const formatCurrency = (amountInPence: number): string => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amountInPence / 100)
}

// Helper to calculate progress percentage
const getProgressPercentage = (raised: number, target: number): number => {
  if (target === 0) return 0
  return Math.min((raised / target) * 100, 100)
}

// Helper to get donation type names from IDs
const getDonationTypeNames = (ids: string[], donationTypes: DonationType[]): string[] => {
  return ids
    .map(id => donationTypes.find(dt => dt.id === id)?.name)
    .filter((name): name is string => name !== undefined)
}

// Helper to get donation type name by ID
const getDonationTypeName = (id: string, donationTypes: DonationType[]): string => {
  return donationTypes.find(dt => dt.id === id)?.name || id
}

// Helper to get platform name
const getPlatformName = (platformId: string): string => {
  return PAYMENT_PLATFORMS.find(p => p.id === platformId)?.name || platformId
}

// Helper to get platform provider
const getPlatformProvider = (platformId: string): string => {
  return PAYMENT_PLATFORMS.find(p => p.id === platformId)?.provider || ''
}

type TimePeriod = 'all_time' | 'this_month' | 'last_month'

interface AnalyticsPanelProps {
  campaignId: string
  donationTypes: DonationType[]
}

const AnalyticsPanel: React.FC<AnalyticsPanelProps> = ({ campaignId, donationTypes }) => {
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('all_time')
  const [analytics, setAnalytics] = useState<CampaignAnalytics | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const data = await campaignsApi.getCampaignAnalytics(campaignId, selectedPeriod)
        setAnalytics(data)
      } catch (err) {
        console.error('Failed to fetch analytics:', err)
        setError('Failed to load analytics')
      } finally {
        setIsLoading(false)
      }
    }

    fetchAnalytics()
  }, [campaignId, selectedPeriod])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <svg className="w-6 h-6 animate-spin text-teal-600" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span className="ml-2 text-sm text-gray-600">Loading analytics...</span>
      </div>
    )
  }

  if (error || !analytics) {
    return (
      <div className="text-center py-6 text-gray-500 text-sm">
        {error || 'No analytics data available for this campaign.'}
      </div>
    )
  }

  const maxDonationTypeAmount = analytics.byDonationType.length > 0
    ? Math.max(...analytics.byDonationType.map(d => d.amount))
    : 0

  return (
    <div className="space-y-4">
      {/* Time Period Filter */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600">Period:</span>
        <div className="flex gap-1">
          {[
            { value: 'all_time', label: 'All Time' },
            { value: 'this_month', label: 'This Month' },
            { value: 'last_month', label: 'Last Month' },
          ].map(period => (
            <button
              key={period.value}
              onClick={() => setSelectedPeriod(period.value as TimePeriod)}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                selectedPeriod === period.value
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {period.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-teal-50 rounded-lg p-3">
          <div className="text-2xl font-bold text-teal-700">
            {formatCurrency(analytics.raisedAmount)}
          </div>
          <div className="text-xs text-teal-600">Donations Received</div>
          <div className="text-xs text-gray-500 mt-0.5">Excludes initial amount</div>
        </div>
        <div className="bg-gray-100 rounded-lg p-3">
          <div className="text-2xl font-bold text-gray-700">
            {analytics.totalTransactions}
          </div>
          <div className="text-xs text-gray-600">Transactions</div>
        </div>
      </div>

      {/* Breakdown Charts */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* By Donation Type */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h5 className="text-sm font-semibold text-gray-700 mb-3">By Donation Type</h5>
          {analytics.byDonationType.length === 0 ? (
            <div className="text-sm text-gray-500 py-2">No donation type breakdown available</div>
          ) : (
            <div className="space-y-3">
              {analytics.byDonationType.map(item => {
                const percentage = maxDonationTypeAmount > 0 ? (item.amount / maxDonationTypeAmount) * 100 : 0
                return (
                  <div key={item.donationTypeId}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-700">{getDonationTypeName(item.donationTypeId, donationTypes)}</span>
                      <span className="font-medium text-gray-900">{formatCurrency(item.amount)}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div
                        className="bg-teal-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">{item.count} transactions</div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* By Platform */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h5 className="text-sm font-semibold text-gray-700 mb-3">By Platform</h5>
          {analytics.byPlatform.filter(p => p.count > 0).length === 0 ? (
            <div className="text-sm text-gray-500 py-2">No platform breakdown available</div>
          ) : (
            <div className="space-y-3">
              {analytics.byPlatform.filter(p => p.count > 0).map(item => {
                const platformsWithData = analytics.byPlatform.filter(p => p.count > 0)
                const maxAmount = Math.max(...platformsWithData.map(p => p.amount))
                const percentage = maxAmount > 0 ? (item.amount / maxAmount) * 100 : 0
                const platformColors: Record<string, string> = {
                  apple_pay: 'bg-gray-800',
                  google_pay: 'bg-blue-500',
                  paypal: 'bg-indigo-500',
                  bank_transfer: 'bg-green-500',
                  stripe: 'bg-blue-500',
                }
                return (
                  <div key={item.platform}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <div>
                        <span className="text-gray-700">{getPlatformName(item.platform)}</span>
                        <span className="text-xs text-gray-400 ml-1">({getPlatformProvider(item.platform)})</span>
                      </div>
                      <span className="font-medium text-gray-900">{formatCurrency(item.amount)}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div
                        className={`${platformColors[item.platform] || 'bg-gray-500'} h-2 rounded-full transition-all duration-300`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">{item.count} transactions</div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

interface DonationTypeSelectorProps {
  donationTypes: DonationType[]
  selectedIds: string[]
  onChange: (ids: string[]) => void
  linkedDonationTypes: Map<string, string> // Map of donation type ID to campaign title that has it linked
  currentCampaignId?: string // ID of the campaign being edited (to exclude from "already linked" check)
}

const DonationTypeSelector: React.FC<DonationTypeSelectorProps> = ({
  donationTypes,
  selectedIds,
  onChange,
  linkedDonationTypes,
  currentCampaignId,
}) => {
  const toggleType = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter(i => i !== id))
    } else {
      onChange([...selectedIds, id])
    }
  }

  // Check if a donation type is linked to another campaign (not the current one being edited)
  const isLinkedToOtherCampaign = (dtId: string): string | null => {
    const linkedCampaign = linkedDonationTypes.get(dtId)
    if (linkedCampaign && !selectedIds.includes(dtId)) {
      return linkedCampaign
    }
    return null
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Linked Donation Types
      </label>
      <p className="text-xs text-gray-500 mb-3">
        Select which donation types contribute to this campaign's progress.
        Each donation type can only be linked to one campaign at a time.
      </p>
      <div className="grid grid-cols-2 gap-2">
        {donationTypes.map(dt => {
          const linkedTo = isLinkedToOtherCampaign(dt.id)
          const isDisabled = linkedTo !== null
          const isSelected = selectedIds.includes(dt.id)

          return (
            <button
              key={dt.id}
              type="button"
              onClick={() => !isDisabled && toggleType(dt.id)}
              disabled={isDisabled}
              className={`p-3 text-left rounded-lg border transition-colors ${
                isSelected
                  ? 'border-teal-500 bg-teal-50 text-teal-700'
                  : isDisabled
                  ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                  isSelected
                    ? 'bg-teal-500 border-teal-500'
                    : isDisabled
                    ? 'border-gray-300 bg-gray-200'
                    : 'border-gray-300'
                }`}>
                  {isSelected && (
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium block">{dt.name}</span>
                  {isDisabled && (
                    <span className="text-xs text-gray-400 block truncate">
                      Linked to: {linkedTo}
                    </span>
                  )}
                </div>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

interface CampaignFormProps {
  campaign: Campaign | null
  donationTypes: DonationType[]
  onSave: (campaign: Campaign) => void
  onCancel: () => void
  isSaving: boolean
  linkedDonationTypes: Map<string, string> // Map of donation type ID to campaign title that has it linked
}

// Status labels for display
const STATUS_LABELS: Record<CampaignStatus, string> = {
  active: 'Active',
  paused: 'Paused',
  completed: 'Completed',
  ended: 'Ended'
}

// Status colors for badges
const STATUS_COLORS: Record<CampaignStatus, { bg: string, text: string }> = {
  active: { bg: 'bg-green-100', text: 'text-green-700' },
  paused: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  completed: { bg: 'bg-blue-100', text: 'text-blue-700' },
  ended: { bg: 'bg-gray-100', text: 'text-gray-600' }
}

// Predefined color options for campaigns
const CAMPAIGN_COLORS = [
  { value: '#4A7C59', label: 'Green' },
  { value: '#2563EB', label: 'Blue' },
  { value: '#7C3AED', label: 'Purple' },
  { value: '#DC2626', label: 'Red' },
  { value: '#EA580C', label: 'Orange' },
  { value: '#CA8A04', label: 'Yellow' },
  { value: '#0D9488', label: 'Teal' },
  { value: '#4B5563', label: 'Gray' },
]

const CampaignForm: React.FC<CampaignFormProps> = ({ campaign, donationTypes, onSave, onCancel, isSaving, linkedDonationTypes }) => {
  const [title, setTitle] = useState(campaign?.title || '')
  // Amounts are stored in pence, display in pounds
  const [targetAmount, setTargetAmount] = useState(campaign ? (campaign.targetAmount / 100).toString() : '')
  const [initialAmount, setInitialAmount] = useState(campaign ? (campaign.initialAmount / 100).toString() : '0')
  const [status, setStatus] = useState<CampaignStatus>(campaign?.status ?? 'paused')
  const [endDate, setEndDate] = useState(campaign?.endDate || '')
  const [linkedDonationTypeIds, setLinkedDonationTypeIds] = useState<string[]>(
    campaign?.linkedDonationTypeIds || []
  )
  const [isDefault, setIsDefault] = useState(campaign?.isDefault || false)
  const [color, setColor] = useState(campaign?.color || '#4A7C59')

  const handleSubmit = () => {
    // Convert pounds to pence for storage
    const newCampaign: Campaign = {
      id: campaign?.id || '', // Backend will generate ID for new campaigns
      title,
      targetAmount: Math.round((parseFloat(targetAmount) || 0) * 100),
      initialAmount: Math.round((parseFloat(initialAmount) || 0) * 100),
      raisedAmount: campaign?.raisedAmount || 0, // Read-only, preserve existing value
      totalRaised: campaign?.totalRaised || 0, // Read-only, preserve existing value
      status,
      isActive: status === 'active', // Computed for backwards compatibility
      endDate, // Keep as-is (empty string clears the date on backend)
      linkedDonationTypeIds,
      isDefault,
      color,
      createdAt: campaign?.createdAt,
      updatedAt: campaign?.updatedAt,
    }

    onSave(newCampaign)
  }

  const isValid = title.trim() !== '' && parseFloat(targetAmount) > 0

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        {campaign ? 'Edit Campaign' : 'Create New Campaign'}
      </h3>
      <div className="space-y-4">
        <TextField
          id="title"
          type="text"
          label="Campaign Title"
          placeholder="e.g. New Masjid Building"
          value={title}
          setValue={setTitle}
        />

        <div className="grid grid-cols-2 gap-4">
          <TextField
            id="targetAmount"
            type="number"
            label="Target Amount (£)"
            placeholder="50000"
            value={targetAmount}
            setValue={setTargetAmount}
          />

          <div>
            <TextField
              id="initialAmount"
              type="number"
              label="Initial Amount (£)"
              placeholder="0"
              value={initialAmount}
              setValue={setInitialAmount}
            />
            <p className="text-xs text-gray-500 mt-1">
              Seed amount not from tracked transactions
            </p>
          </div>
        </div>

        <div>
          <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
            End Date (Optional)
          </label>
          <div className="flex gap-2">
            <input
              id="endDate"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            {endDate && (
              <button
                type="button"
                onClick={() => setEndDate('')}
                className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-300"
                title="Clear end date"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Campaign will automatically become inactive after this date and won't be displayed on the iOS/Android app
          </p>
        </div>

        <DonationTypeSelector
          donationTypes={donationTypes}
          selectedIds={linkedDonationTypeIds}
          onChange={setLinkedDonationTypeIds}
          linkedDonationTypes={linkedDonationTypes}
          currentCampaignId={campaign?.id}
        />

        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Campaign Status
          </label>
          <div className="grid grid-cols-2 gap-2">
            {(['active', 'paused', 'completed', 'ended'] as CampaignStatus[]).map(s => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                className={`p-3 text-left rounded-lg border transition-colors ${
                  status === s
                    ? 'border-teal-500 bg-teal-50 text-teal-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                    status === s
                      ? 'bg-teal-500 border-teal-500'
                      : 'border-gray-300'
                  }`}>
                    {status === s && (
                      <div className="w-2 h-2 bg-white rounded-full" />
                    )}
                  </div>
                  <span className="text-sm font-medium">{STATUS_LABELS[s]}</span>
                </div>
                <p className="text-xs text-gray-500 ml-6 mt-1">
                  {s === 'active' && 'Visible to donors, accepting donations'}
                  {s === 'paused' && 'Temporarily hidden, can reactivate'}
                  {s === 'completed' && 'Target reached, campaign successful'}
                  {s === 'ended' && 'Campaign over, target not reached'}
                </p>
              </button>
            ))}
          </div>
          {endDate && (
            <p className="text-xs text-gray-500 mt-3">
              Campaigns with a past end date will be automatically marked as ended.
            </p>
          )}
        </div>

        {/* Default Campaign Toggle */}
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-blue-800">
                Default Campaign
              </label>
              <p className="text-xs text-blue-600 mt-1">
                Shown in the app when no donation types are selected
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIsDefault(!isDefault)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isDefault ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isDefault ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Campaign Color */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Campaign Color
          </label>
          <p className="text-xs text-gray-500 mb-3">
            Select a color for the campaign widget displayed in the app
          </p>
          <div className="flex flex-wrap gap-2">
            {CAMPAIGN_COLORS.map(c => (
              <button
                key={c.value}
                type="button"
                onClick={() => setColor(c.value)}
                className={`w-10 h-10 rounded-lg border-2 transition-all ${
                  color === c.value
                    ? 'border-gray-800 ring-2 ring-offset-2 ring-gray-400'
                    : 'border-transparent hover:border-gray-300'
                }`}
                style={{ backgroundColor: c.value }}
                title={c.label}
              />
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Selected: {CAMPAIGN_COLORS.find(c => c.value === color)?.label || color}
          </p>
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            id="saveCampaign"
            label={campaign ? 'Update Campaign' : 'Create Campaign'}
            isLoading={isSaving}
            onClick={handleSubmit}
            disabled={!isValid || isSaving}
          />
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

interface CampaignCardProps {
  campaign: Campaign
  donationTypes: DonationType[]
  onEdit: () => void
  onDelete: () => void
  onStatusChange: (newStatus: CampaignStatus) => void
  isUpdatingStatus: boolean
}

const CampaignCard: React.FC<CampaignCardProps> = ({
  campaign,
  donationTypes,
  onEdit,
  onDelete,
  onStatusChange,
  isUpdatingStatus,
}) => {
  const [showAnalytics, setShowAnalytics] = useState(false)
  const [showStatusMenu, setShowStatusMenu] = useState(false)

  const progress = getProgressPercentage(campaign.totalRaised, campaign.targetAmount)
  const linkedTypeNames = getDonationTypeNames(campaign.linkedDonationTypeIds, donationTypes)
  const statusColor = STATUS_COLORS[campaign.status]

  return (
    <div className={`bg-white rounded-lg border p-5 ${campaign.status === 'active' ? 'border-green-300 ring-1 ring-green-100' : 'border-gray-200'}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div
              className="w-4 h-4 rounded-full border border-gray-200 flex-shrink-0"
              style={{ backgroundColor: campaign.color || '#4A7C59' }}
              title={`Campaign color: ${campaign.color || '#4A7C59'}`}
            />
            <h4 className="font-semibold text-gray-800 text-lg">{campaign.title}</h4>
            <span className={`text-xs ${statusColor.bg} ${statusColor.text} px-2 py-0.5 rounded-full font-medium`}>
              {STATUS_LABELS[campaign.status]}
            </span>
            {campaign.isDefault && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                Default
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 ml-4">
          {/* Status dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowStatusMenu(!showStatusMenu)}
              disabled={isUpdatingStatus}
              className={`p-2 rounded hover:bg-gray-100 disabled:opacity-50 ${campaign.status === 'active' ? 'text-green-600' : 'text-gray-400'}`}
              title="Change status"
            >
              {isUpdatingStatus ? (
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
              )}
            </button>
            {showStatusMenu && (
              <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                {(['active', 'paused', 'completed', 'ended'] as CampaignStatus[]).map(s => (
                  <button
                    key={s}
                    onClick={() => {
                      onStatusChange(s)
                      setShowStatusMenu(false)
                    }}
                    disabled={s === campaign.status}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-50 disabled:bg-gray-50 ${
                      s === campaign.status ? 'font-medium text-teal-600' : 'text-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${STATUS_COLORS[s].bg.replace('100', '500')}`}></span>
                      {STATUS_LABELS[s]}
                      {s === campaign.status && ' (current)'}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={onEdit}
            className="p-2 rounded hover:bg-gray-100 text-gray-600"
            title="Edit"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={onDelete}
            className="p-2 rounded hover:bg-gray-100 text-red-600"
            title="Delete"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Linked Donation Types */}
      {linkedTypeNames.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="text-xs text-gray-500">Linked to:</span>
          {linkedTypeNames.map(name => (
            <span
              key={name}
              className="text-xs bg-teal-50 text-teal-700 px-2 py-1 rounded"
            >
              {name}
            </span>
          ))}
        </div>
      )}

      {/* Progress Section */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Progress</span>
          <span className="text-sm text-gray-600">{progress.toFixed(1)}%</span>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
          <div
            className="bg-green-500 h-3 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Amounts */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-gray-800">
              {formatCurrency(campaign.totalRaised)}
            </span>
            <span className="text-gray-500">raised</span>
          </div>
          <div className="text-right">
            <span className="text-gray-500">of </span>
            <span className="font-semibold text-gray-700">{formatCurrency(campaign.targetAmount)}</span>
            <span className="text-gray-500"> goal</span>
          </div>
        </div>

        {/* Amount Breakdown */}
        <div className="border-t border-gray-200 pt-3 space-y-1">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Initial amount</span>
            <span className="text-gray-700">{formatCurrency(campaign.initialAmount)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Tracked donations</span>
            <span className="text-gray-700">{formatCurrency(campaign.raisedAmount)}</span>
          </div>
        </div>
      </div>

      {/* Analytics Toggle */}
      <div className="mt-4">
        <button
          onClick={() => setShowAnalytics(!showAnalytics)}
          className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700 font-medium"
        >
          <svg
            className={`w-4 h-4 transition-transform ${showAnalytics ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          {showAnalytics ? 'Hide Analytics' : 'View Analytics'}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </button>
      </div>

      {/* Analytics Panel */}
      {showAnalytics && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <AnalyticsPanel campaignId={campaign.id} donationTypes={donationTypes} />
        </div>
      )}

      {/* Footer */}
      {(campaign.createdAt || campaign.endDate) && (
        <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500 flex justify-between">
          {campaign.createdAt && (
            <span>
              Created: {new Date(campaign.createdAt).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}
            </span>
          )}
          {campaign.endDate && (
            <span className={new Date(campaign.endDate) < new Date() ? 'text-red-500' : 'text-gray-500'}>
              {new Date(campaign.endDate) < new Date() ? 'Ended' : 'Ends'}: {new Date(campaign.endDate).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

const CampaignsPage: React.FC = () => {
  const [donationTypes, setDonationTypes] = useState<DonationType[]>([])
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null)
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [togglingId, setTogglingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Fetch campaigns and donation types on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const [campaignsData, donationTypesData] = await Promise.all([
          campaignsApi.listCampaigns(),
          donationsApi.listDonationTypes()
        ])
        setCampaigns(campaignsData)
        // Convert API donation types to local DonationType format
        setDonationTypes(donationTypesData.map((dt: ApiDonationType) => ({
          id: dt.id,
          name: dt.name,
          description: dt.description,
          iconName: dt.iconName || '',
          suggestedAmounts: dt.suggestedAmounts,
          minimumAmount: dt.minimumAmount,
          allowCustomAmount: dt.allowCustomAmount,
          allowRecurring: dt.allowRecurring || false,
          recurringFrequencies: dt.recurringFrequencies || [],
          isActive: dt.isActive,
          order: dt.order,
        })))
      } catch (err) {
        console.error('Failed to fetch data:', err)
        setError('Failed to load campaigns. Please try again.')
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [])

  const handleSave = async (campaign: Campaign) => {
    try {
      setIsSaving(true)
      setError(null)

      if (editingCampaign) {
        // Update existing campaign
        const updated = await campaignsApi.updateCampaign(editingCampaign.id, campaign)
        setCampaigns(prev => prev.map(c => c.id === updated.id ? updated : c))
      } else {
        // Create new campaign
        const created = await campaignsApi.createCampaign(campaign)
        setCampaigns(prev => [...prev, created])
      }

      setEditingCampaign(null)
      setIsAddingNew(false)
    } catch (err: any) {
      console.error('Failed to save campaign:', err)
      // Check for donation type already linked error
      if (err?.message?.includes('already linked')) {
        setError('One or more donation types are already linked to another campaign.')
      } else {
        setError('Failed to save campaign. Please try again.')
      }
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this campaign? This cannot be undone.')) {
      return
    }

    try {
      setError(null)
      await campaignsApi.deleteCampaign(id)
      setCampaigns(prev => prev.filter(c => c.id !== id))
    } catch (err) {
      console.error('Failed to delete campaign:', err)
      setError('Failed to delete campaign. Please try again.')
    }
  }

  const handleStatusChange = async (id: string, newStatus: CampaignStatus) => {
    try {
      setTogglingId(id)
      setError(null)

      const updated = await campaignsApi.updateCampaign(id, { status: newStatus })
      setCampaigns(prev => prev.map(c => c.id === updated.id ? updated : c))
    } catch (err) {
      console.error('Failed to update campaign status:', err)
      setError('Failed to update campaign. Please try again.')
    } finally {
      setTogglingId(null)
    }
  }

  const handleCancel = () => {
    setEditingCampaign(null)
    setIsAddingNew(false)
  }

  const showForm = isAddingNew || editingCampaign !== null
  const activeCampaigns = campaigns.filter(c => c.status === 'active')

  // Display order: active campaigns first, then everything else; within each group, newest first.
  const sortedCampaigns = [...campaigns].sort((a, b) => {
    const aActive = a.status === 'active' ? 0 : 1
    const bActive = b.status === 'active' ? 0 : 1
    if (aActive !== bActive) return aActive - bActive
    const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0
    const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0
    return bTime - aTime
  })

  // Build a map of donation type ID -> campaign title for donation types that are already linked.
  // Only consider ACTIVE campaigns — the backend's validateDonationTypesNotLinked (campaign.go)
  // only blocks active campaigns, so types linked to ended/paused/completed campaigns should be
  // re-selectable. Exclude the campaign being edited from the map.
  const linkedDonationTypes = new Map<string, string>()
  activeCampaigns.forEach(c => {
    // Skip the campaign being edited
    if (editingCampaign && c.id === editingCampaign.id) return
    c.linkedDonationTypeIds.forEach(dtId => {
      linkedDonationTypes.set(dtId, c.title)
    })
  })

  return (
    <div className="flex flex-col h-screen">
      <Navbar />
      <div className="md:container mx-auto flex-1 py-8 px-4">
        <div className="w-full max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-xl font-bold text-gray-800">Campaigns</h1>
              <p className="text-gray-600 text-sm mt-1">
                Manage fundraising campaigns with progress tracking
              </p>
            </div>
            {!showForm && !isLoading && (
              <button
                onClick={() => setIsAddingNew(true)}
                className="inline-flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Campaign
              </button>
            )}
          </div>

          {/* Error Banner */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-800">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <svg className="w-8 h-8 animate-spin text-teal-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="ml-3 text-gray-600">Loading campaigns...</span>
            </div>
          )}

          {!isLoading && (
            <>
              {/* Active Campaigns Banner */}
              {activeCampaigns.length > 0 && !showForm && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-800 mb-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-medium">
                      {activeCampaigns.length === 1 ? 'Active Campaign:' : `${activeCampaigns.length} Active Campaigns:`}
                    </span>
                  </div>
                  <div className="space-y-1 ml-7">
                    {activeCampaigns.map(c => (
                      <div key={c.id} className="flex items-center gap-2 text-green-700">
                        <span>{c.title}</span>
                        <span className="text-green-600 text-sm">
                          ({getProgressPercentage(c.totalRaised, c.targetAmount).toFixed(0)}% funded)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Form */}
              {showForm && (
                <div className="mb-6">
                  <CampaignForm
                    campaign={editingCampaign}
                    donationTypes={donationTypes}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    isSaving={isSaving}
                    linkedDonationTypes={linkedDonationTypes}
                  />
                </div>
              )}

              {/* Campaign List */}
              <div className="space-y-4">
                {sortedCampaigns.map(campaign => (
                  <CampaignCard
                    key={campaign.id}
                    campaign={campaign}
                    donationTypes={donationTypes}
                    onEdit={() => setEditingCampaign(campaign)}
                    onDelete={() => handleDelete(campaign.id)}
                    onStatusChange={(newStatus) => handleStatusChange(campaign.id, newStatus)}
                    isUpdatingStatus={togglingId === campaign.id}
                  />
                ))}
              </div>

              {campaigns.length === 0 && !showForm && (
                <div className="text-center py-12 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <p>No campaigns yet. Click "New Campaign" to create one.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default CampaignsPage
